import React, { useState, useEffect, useRef } from "react";
import store from "../../../store";
import { Grid, Alert, Snackbar } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import WeatherLoader from "../../../components/loader";
import "./_index.scss";

interface ClientNamesInterface {
  client_name: string;
  client_id: string;
}

const normalizeDateTime = (val: string) => {
  if (!val) return "";
  return val.slice(0, 16);
};

const CustomSelect: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, options, onChange, placeholder = "Select...", disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value?.toString() === value?.toString());

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      className={`eu-custom-select ${isOpen ? "eu-custom-select--open" : ""} ${disabled ? "eu-custom-select--disabled" : ""}`}
      ref={ref}
    >
      <button
        type="button"
        className={`eu-input-box eu-input-box--select-btn ${isOpen ? "eu-input-box--focused" : ""}`}
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
      >
        <span className={selected ? "eu-select-val" : "eu-select-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`eu-select-arrow ${isOpen ? "eu-select-arrow--open" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div className="eu-select-dropdown">
          {options.length === 0
            ? <div className="eu-select-empty">No options available</div>
            : options.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`eu-select-option ${value?.toString() === opt.value?.toString() ? "eu-select-option--active" : ""}`}
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                >
                  <span className="eu-select-dot" />
                  <span>{opt.label}</span>
                  {value?.toString() === opt.value?.toString() && (
                    <svg className="eu-select-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              ))
          }
        </div>
      )}
    </div>
  );
};

const EditUserComponent = () => {
  const [sn, setSn] = useState(localStorage.getItem("sideNav"));
  const data = useSelector((state: any) => state?.app);
  const windowWidth = useRef(window.innerWidth);
  const [open, setOpen] = useState(windowWidth.current > 1000 ? true : false);
  const [clientId, setClientId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "", password: "", user_type: "", email_address: "",
    client: "", operation: "", start_date: "", end_date: "",
    expected_date: "", client_id: "", forecast_id: "",
  });
  const { id } = useParams();
  const [clientNames, setClientNames] = useState<ClientNamesInterface[]>([]);
  const [operationsData, setOperationsData] = useState<any[]>([]);
  const [filesData, setFilesData] = useState({ wind: "", wave: "", current: "", satpic: "" });
  const [selectedClientName, setSelectedClientName] = useState(false);
  const [selectedForecast_id, setSelectedForecast_id] = useState(false);
  const navigate = useNavigate();
  const clientRef = useRef<any>(null);
  const foreRef = useRef<any>(null);
  const [submitError, setSubmitError] = useState("");
  const [dateWarning, setDateWarning] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [usernameError, setUsernameError] = useState("");

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault();

  const validateDates = (startDate: any, expectedEndDate: any) => {
    if (startDate && expectedEndDate && new Date(expectedEndDate) < new Date(startDate)) {
      setDateWarning("Expected End Date cannot be earlier than Start Date.");
      return false;
    } else {
      setDateWarning("");
      return true;
    }
  };

  const getClientNames = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/getclients/`);
      setClientNames(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  useEffect(() => { getClientNames(); }, []);

  const checkUsernameExists = async (username: string) => {
    if (!username || username.trim() === "") { setUsernameError(""); return; }
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/user/get/`);
      const allUsers = response.data;
      const usernameExists = allUsers.some(
        (user: any) => user.name.toLowerCase() === username.toLowerCase() && user.id?.toString() !== id?.toString()
      );
      setUsernameError(usernameExists ? "Username already exists" : "");
    } catch (error) {
      console.error("Error checking username:", error);
    }
  };

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);
    if (name === "expected_date" || name === "start_date") {
      const startDate = name === "start_date" ? value : formData.start_date;
      const expectedDate = name === "expected_date" ? value : formData.expected_date;
      validateDates(startDate, expectedDate);
    }
    if (name === "name") await checkUsernameExists(value);
  };

  const getClientData = async (clientId: any) => {
    if (!clientId) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/operations12/${clientId}/`);
      setOperationsData(response.data);
    } catch (error) {
      console.error("Error fetching operations:", error);
    }
  };

  const getFiles = async (forecastId: any) => {
    if (!forecastId) return;
    setFilesData({ wind: "", wave: "", satpic: "", current: "" });
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/getfiles/${forecastId}/`);
      setFilesData(response.data);
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          setIsLoading(true);
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/user/get/${id}/`, {
            headers: { "Content-Type": "application/json" },
          });
          const userData = response.data;

          if (userData.client_id) {
            const opsResponse = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/operations12/${userData.client_id}/`);
            let ops = opsResponse.data;
            const exists = ops.some((o: any) => o.forecast_id?.toString() === userData.forecast_id?.toString());
            if (!exists && userData.forecast_id && userData.operation) {
              ops = [
                { forecast_id: userData.forecast_id, forecast_description: userData.operation, client_id: userData.client_id },
                ...ops,
              ];
            }
            setOperationsData(ops);
          }

          setFormData({
            ...userData,
            forecast_id: userData.forecast_id?.toString() || "",
            client_id: userData.client_id?.toString() || "",
          });

          setFilesData({ wind: userData.wind, wave: userData.wave, current: userData.current, satpic: userData.satpic });
          if (userData.client) setSelectedClientName(true);
          if (userData.forecast_id || userData.operation) setSelectedForecast_id(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSubmit = async () => {
    if (usernameError) { setSubmitError("Username already exists. Please choose a different username."); return; }
    if (formData.start_date && formData.expected_date && new Date(formData.expected_date) < new Date(formData.start_date)) {
      setSubmitError("Expected End Date cannot be earlier than Start Date."); return;
    } else { setSubmitError(""); }

    const newData = {
      ...formData,
      name: formData.name || "",
      password: formData.password ? formData.password.toString() : "",
      user_type: formData.user_type ? formData.user_type.toString() : "",
      email_address: formData.email_address ? formData.email_address.toString() : "",
      client: formData.client ? formData.client.toString() : "",
      operation: formData.operation ? formData.operation.toString() : "",
      start_date: formData.start_date || "",
      end_date: formData.end_date || "",
      expected_date: formData.expected_date || "",
      client_id: formData.client_id ? formData.client_id.toString() : "",
      forecast_id: formData.forecast_id ? formData.forecast_id.toString() : "",
    };

    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_IP}/api/user/get/${id}/`, newData, {
        headers: { "Content-Type": "application/json" },
      });
      setShowSuccessAlert(true);
      setTimeout(() => navigate("/usermanagement"), 1500);
    } catch (error) {
      console.error("Error updating user:", error);
      setSubmitError("Failed to update user. Please try again.");
    }
  };

  useEffect(() => { setSn(localStorage.getItem("sideNav")); }, []);
  useEffect(() => { store.dispatch({ type: "TOGGLE_MENU", payload: windowWidth.current > 1000 ? true : false }); }, []);
  useEffect(() => { setOpen(data.toggle); }, [data]);
  useEffect(() => { if (formData.client_id) getClientData(formData.client_id); }, [formData.client_id]);

  if (isLoading) {
    return (
      <div style={open ? { marginLeft: 260, width: "calc(100% - 260px)" } : {}} className="mainas">
        <WeatherLoader />
      </div>
    );
  }

  return (
    <div style={open ? { marginLeft: 260, width: "calc(100% - 260px)" } : {}} className="mainas">
      <div className="eu-wrap">

        <div className="eu-header">
          <div className="eu-header-left">
            <button className="eu-back-btn" onClick={() => navigate("/usermanagement")}>
              <ArrowBackIcon />
            </button>
            <div>
              <h1 className="eu-heading">Edit User</h1>
              <p className="eu-subheading">Update user information and access</p>
            </div>
          </div>
          <button className="eu-save-btn" onClick={handleSubmit} disabled={!!usernameError}>
            <SaveIcon style={{ fontSize: 16 }} />
            Update User
          </button>
        </div>

        <div className="eu-card">
          <Grid container spacing={3}>

            <Grid item xs={12} md={6}>
              <label className="eu-label">Client</label>
              {selectedClientName ? (
                <div className="eu-input-box" onClick={() => clientRef.current?.focus()}>
                  <input ref={clientRef} className="eu-input" value={formData.client} placeholder="Select client" readOnly onChange={() => {}} />
                </div>
              ) : (
                <div className="eu-input-box" onClick={() => {
                  setSelectedClientName(true);
                  setTimeout(() => clientRef.current && clientRef.current.focus(), 100);
                  const new_obj: any = {};
                  Object.keys(formData).forEach(key => (new_obj[key] = ""));
                  setFormData(new_obj);
                  const new_files_obj: any = {};
                  Object.keys(filesData).forEach(key => (new_files_obj[key] = ""));
                  setFilesData(new_files_obj);
                }}>
                  <input className="eu-input" value={formData.client} placeholder="Select client" readOnly onChange={() => {}} />
                </div>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <label className="eu-label">Project Location</label>
              <CustomSelect
                value={formData.forecast_id?.toString() || ""}
                options={operationsData.map(o => ({ value: o.forecast_id?.toString(), label: o.forecast_description }))}
                onChange={v => {
                  const n = operationsData.find(o => o.forecast_id?.toString() === v);
                  if (n) {
                    setFormData({
                      ...formData,
                      start_date: n?.start_date || formData.start_date,
                      forecast_id: n?.forecast_id?.toString(),
                      expected_date: n?.expected_end_date || formData.expected_date,
                      operation: n?.forecast_description,
                      client_id: n?.client_id?.toString() || formData.client_id,
                    });
                    if (n.forecast_id) getFiles(n.forecast_id);
                  }
                }}
                placeholder="Select project location"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label className="eu-label">Username</label>
              <div className={`eu-input-box ${usernameError ? "eu-input-box--error" : ""}`}>
                <input className="eu-input" name="name" value={formData.name}
                  onChange={handleInputChange as any} placeholder="Enter username" />
              </div>
              {usernameError && <p className="eu-field-error">{usernameError}</p>}
            </Grid>

            <Grid item xs={12} md={6}>
              <label className="eu-label">Password</label>
              <div className="eu-input-box eu-input-box--addon">
                <input className="eu-input" name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password} onChange={handleInputChange as any}
                  placeholder="Leave blank to keep current password" />
                <button type="button" className="eu-pass-toggle"
                  onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} tabIndex={-1}>
                  {showPassword ? <VisibilityOff style={{ fontSize: 18 }} /> : <Visibility style={{ fontSize: 18 }} />}
                </button>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <label className="eu-label">User Type</label>
              <CustomSelect
                value={formData.user_type}
                options={[{ value: "user", label: "User" }, { value: "admin", label: "Admin" }]}
                onChange={v => setFormData({ ...formData, user_type: v })}
                placeholder="Select type"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label className="eu-label">Start Date</label>
              <div className="eu-input-box">
                <input className="eu-input" name="start_date" type="datetime-local"
                  value={formData.start_date} onChange={handleInputChange as any} />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <label className="eu-label">Email Address</label>
              <div className="eu-input-box">
                <input className="eu-input" name="email_address" type="email"
                  value={formData.email_address} onChange={handleInputChange as any}
                  placeholder="Enter email address" />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <label className="eu-label">Expected End Date</label>
              <div className="eu-input-box">
                <input className="eu-input" name="expected_date" type="datetime-local"
                  value={formData.expected_date} onChange={handleInputChange as any} />
              </div>
            </Grid>

          </Grid>

          {submitError && <Alert severity="error" sx={{ mt: 3, borderRadius: "12px" }}>{submitError}</Alert>}
          {dateWarning && <Alert severity="warning" sx={{ mt: 3, borderRadius: "12px" }}>{dateWarning}</Alert>}
        </div>

      </div>

      <Snackbar open={showSuccessAlert} autoHideDuration={1500} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="success" sx={{ borderRadius: "12px" }}>User updated successfully!</Alert>
      </Snackbar>
    </div>
  );
};

export default EditUserComponent;